import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { exhaustMap, map, catchError, mergeMap } from "rxjs/operators";

import * as ReportsActionTypes from "../../actions/reports/reports.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

import { ReportsService } from "src/app/shared/services/reports/reports.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class ReportsEffects {
  // Get Reports List
  getReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.getReportsList),
      exhaustMap((action) =>
        this.reportsService.getReports().pipe(
          map((result) =>
            ReportsActionTypes.loadReportsList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ReportsActionTypes.loadReportsList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj("Reports.ReportLoadError", SnackbarType.ERROR)
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Generate Report
  generateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.generateReport),
      exhaustMap((action) =>
        this.reportsService.generateReport(action.report, action.options).pipe(
          map((result) => {
            return ReportsActionTypes.loadGeneratedReport({ response: result });
          }),
          catchError((error) => {
            const timeoutError = this.isTimeoutError(error);
            const actions = [
              ReportsActionTypes.loadGeneratedReport({
                response: { data: undefined, timestamp: new Date().getTime() },
              }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  timeoutError ? error : "Reports.GeneratingReportError",
                  timeoutError? SnackbarType.INFO : SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Reports Status List
  getReportsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.getReportStatusList),
      exhaustMap((action) =>
        this.reportsService
          .getReportsStatus(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ReportsActionTypes.loadReportStatusList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ReportsActionTypes.loadReportStatusList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Reports.ReportLoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Reports Status Excel List
  getSitesExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.getReportStatusExcelList),
      exhaustMap((action) =>
        this.reportsService
          .getReportsStatus(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ReportsActionTypes.loadReportStatusExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ReportsActionTypes.loadReportStatusExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Reports.ReportLoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Reports Status Table Config
  getReportStatusTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.getReportStatusTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              ReportsActionTypes.loadReportStatusTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ReportsActionTypes.loadReportStatusTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Reports Status Table Config
  saveReportStatusTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.saveReportStatusTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            ReportsActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              ReportsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Report Data
  getReportData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActionTypes.getReportData),
      exhaustMap((action) =>
        this.reportsService.getReportData(action.code).pipe(
          map((result) => ReportsActionTypes.loadReportData({ response: result })),
          catchError((error) => {
            const actions = [
              ReportsActionTypes.loadReportData({ response: null }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Reports.ReportDataLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private reportsService: ReportsService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }

  private isTimeoutError(error: any) {
    return error && error == 'Reports.GeneratingReportTimeout';
  }
}
